import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SectionCard, InfoRow, Badge, formatDate, formatDateTime, formatINR } from "../UI/index";
import {useUpdateSurveyStatus} from "../../../hooks/useWorkshopQueries";



const surveyBadgeVariant = s => ({
  COMPLETED: "green", ACCEPTED: "blue", ASSIGNED: "blue",
  PENDING: "yellow", RESCHEDULED: "yellow",
  REJECTED: "red", CANCELLED: "red",
}[s] || "gray");

// ─────────────────────────────────────────────────────────────────────────────
// Draft builder — auto-fills from case data
// ─────────────────────────────────────────────────────────────────────────────
export function buildDraft({ caseInfo, insuranceDetails, accidentDetails, documents, thirdParty }) {
  const policyNo    = caseInfo?.policyNo  || insuranceDetails?.policyNo  || "N/A";
  const vehicleNo   = caseInfo?.vehicleNo  || "N/A";
  const vehicleMake = caseInfo?.vehicleMake || "N/A";
  const insuredName = caseInfo?.customerName || "N/A";
  const company     = insuranceDetails?.company || caseInfo?.insuranceCompany || "Insurance Company";
  const accDate     = caseInfo?.accidentDate
    ? new Date(caseInfo.accidentDate).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })
    : "N/A";
  const accTime     = caseInfo?.accidentTime || "N/A";
  const location    = caseInfo?.location    || "N/A";
  const description = accidentDetails?.description || "N/A";
  const caseNo      = caseInfo?.accidentCaseNo || "N/A";
  const today       = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const tpLine      = thirdParty ? "YES — Third party property/bodily loss reported" : "NO";
  const docList     = (documents || [])
    .map((d, i) => `  ${i + 1}. ${(d.type || "Document").replace(/_/g, " ")} — ${d.fileName || d.fileUrl || "attached"}`)
    .join("\n");

  return `Subject: Accident Claim Intimation — Policy No. ${policyNo} | Vehicle: ${vehicleNo}

Dear Claims Department,
${company},

We hereby formally intimate you of a motor accident claim and request assignment of a spot surveyor at the earliest.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
POLICY & VEHICLE DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Policy Number      : ${policyNo}
Insured Name       : ${insuredName}
Vehicle Number     : ${vehicleNo}
Vehicle Make/Model : ${vehicleMake}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ACCIDENT DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Date of Accident   : ${accDate}
Time of Accident   : ${accTime}
Location           : ${location}
Third Party Loss   : ${tpLine}
Description        : ${description}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
DOCUMENTS ATTACHED
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${docList || "  No documents selected."}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Kindly register this claim and share the assigned surveyor details (name, contact, and scheduled inspection date) so the vehicle assessment can proceed without delay.

Internal Case Ref   : ${caseNo}
Intimation Date     : ${today}

Warm regards,
ClaimPro Assist

---
Generated by ClaimPro CRM · Case Ref: ${caseNo}`;
}

export function SkipReasonForm({ title, warningText, confirmLabel, onCancel, onConfirm, saving }) {
  const [reason, setReason] = useState("");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        padding: "10px 14px", borderRadius: 10, fontSize: 12,
        background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.2)",
        color: "var(--wd-red)",
      }}>
        ⚠️ {warningText}
      </div>
      <div>
        <SectionLabel>Reason for skipping *</SectionLabel>
        <textarea
          className="wd-input" rows={3}
          style={{ resize: "vertical", fontSize: 13 }}
          placeholder="e.g. Vehicle brought directly to workshop by owner, insurer not involved for this case…"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button
          className="wd-btn wd-btn-danger wd-btn-sm"
          onClick={() => onConfirm(reason)}
          disabled={saving || !reason.trim()}
        >
          {saving ? "Saving…" : confirmLabel}
        </button>
      </div>
    </div>
  );
}

export function SurveysSkippedBanner({ reason, onUndo }) {
  return (
    <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(100,116,139,.3)" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
        background: "rgba(100,116,139,.08)", borderBottom: "1px solid rgba(100,116,139,.15)",
      }}>
        <span style={{ fontSize: 16 }}>⏭️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t2)" }}>
            Intimation & Surveys Skipped
          </div>
          <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
            Case moved directly to Workshop Repair
          </div>
        </div>
      </div>
      {reason && (
        <div style={{ padding: "12px 16px", background: "var(--wd-card)" }}>
          <SectionLabel>Reason</SectionLabel>
          <div style={{ fontSize: 13, color: "var(--wd-t2)", lineHeight: 1.6 }}>{reason}</div>
        </div>
      )}
    </div>
  );
}

export function SpotSurveyStatus({ surveys, onGoToSurveys }) {
  const spotSurvey = (surveys || []).find(s =>
    (s.type || "").toLowerCase().includes("spot") || s.type === "SPOT_SURVEY"
  );
  const isDone = spotSurvey?.status === "COMPLETED" || !!spotSurvey?.completedAt;
  const completedDate = spotSurvey?.completedAt
    ? new Date(spotSurvey.completedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;
 
  if (isDone) {
    return (
      <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(34,197,94,.25)" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
          background: "rgba(34,197,94,.08)", borderBottom: "1px solid rgba(34,197,94,.18)",
        }}>
          <span style={{ fontSize: 16 }}>&#128269;</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-green)" }}>Spot Survey Completed</div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
              {"Marked by admin" + (completedDate ? " · " + completedDate : "")}
            </div>
          </div>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11 }} onClick={onGoToSurveys}>
            View in Surveys →
          </button>
        </div>
        <div style={{ padding: "14px 16px", background: "var(--wd-card)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 }}>Surveyor</div>
            <div style={{ fontSize: 13, color: "var(--wd-t1)", fontWeight: 500 }}>{spotSurvey?.surveyorName || spotSurvey?.surveyorId || "—"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 }}>Survey Type</div>
            <div style={{ fontSize: 13, color: "var(--wd-t1)", fontWeight: 500 }}>{(spotSurvey?.type || "Spot Survey").replace(/_/g, " ")}</div>
          </div>
          {spotSurvey?.remarks && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 }}>Remarks</div>
              <div style={{ fontSize: 13, color: "var(--wd-t2)", lineHeight: 1.6 }}>{spotSurvey.remarks}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
 
  return (
    <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(168,85,247,.22)" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
        background: "rgba(168,85,247,.07)", borderBottom: "1px solid rgba(168,85,247,.15)",
      }}>
        <span style={{ fontSize: 16 }}>&#128269;</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-purple, #a855f7)" }}>Awaiting Spot Survey Completion</div>
          <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
            Surveyor assigned · Survey not yet marked as completed
          </div>
        </div>
        {spotSurvey?.status && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
            background: "rgba(168,85,247,.1)", color: "var(--wd-purple, #a855f7)",
            border: "1px solid rgba(168,85,247,.2)",
          }}>
            {spotSurvey.status}
          </span>
        )}
      </div>
      <div style={{ padding: "14px 16px", background: "var(--wd-card)", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 12, color: "var(--wd-t2)", lineHeight: 1.6 }}>
          Admin needs to mark the spot survey as done once the surveyor has completed the inspection.
          Go to the <strong>Surveys tab</strong> to update the survey status.
        </div>
        <button
          className="wd-btn wd-btn-primary wd-btn-sm"
          style={{ display: "inline-flex", alignItems: "center", gap: 5, alignSelf: "flex-start" }}
          onClick={onGoToSurveys}
        >
          Go to Surveys Tab
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

function SurveyActionForm({ survey, onClose, onSaved }) {
  const [mode, setMode]         = useState(null); // null | "complete" | "reschedule"
  const [saving, setSaving]     = useState(false);

  const updateSurvey = useUpdateSurveyStatus();
  const today = new Date().toISOString().slice(0, 10);
 
  // complete form
  const [compDate,   setCompDate]   = useState(today);
  const [compTime,   setCompTime]   = useState(new Date().toTimeString().slice(0, 5));
  const [compRemark, setCompRemark] = useState(survey.remarks || "");
 
  // reschedule form
  const [reschedDate, setReschedDate] = useState("");
  const [reschedTime, setReschedTime] = useState("09:00");
  const [reschedNote, setReschedNote] = useState("");
 
  const handleComplete = useCallback(async () => {
    setSaving(true);
    try {
      // 🔌 Replace:
      await updateSurvey.mutateAsync({
        surveyId: survey.id, 
        status: "COMPLETED",
        remarks: compRemark,
        surveyCompletedDate: `${compDate}T${compTime}`,
      });
      await new Promise(r => setTimeout(r, 700));
      onSaved?.();
      onClose();
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [survey.id, compDate, compTime, compRemark, onSaved, onClose]);
 
  const handleReschedule = useCallback(async () => {
    setSaving(true);
    try {
      await updateSurvey.mutateAsync({
        surveyId: survey.id,
        status: "RESCHEDULED",
        remarks: reschedNote,           
       scheduledAt: reschedDate ? `${reschedDate}T${reschedTime}` : null,
      });
      onSaved?.();
      onClose();
    } catch (e) { console.error(e); }
    setSaving(false);
}, [survey.id, reschedDate,reschedTime, reschedNote, onSaved, onClose]);
 
  if (!mode) {
    return (
      <div style={{
        marginTop: 14, paddingTop: 14,
        borderTop: "1px dashed var(--wd-border)",
        display: "flex", gap: 8,
      }}>
        <button
          className="wd-btn wd-btn-primary wd-btn-sm"
          onClick={() => setMode("complete")}
        >
          ✓ Mark as Completed
        </button>
        <button
          className="wd-btn wd-btn-ghost wd-btn-sm"
          onClick={() => setMode("reschedule")}
        >
          📅 Reschedule
        </button>
        <button
          className="wd-btn wd-btn-ghost wd-btn-sm"
          style={{ marginLeft: "auto" }}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    );
  }
 
  if (mode === "complete") {
    return (
      <div style={{
        marginTop: 14, paddingTop: 14,
        borderTop: "1px dashed var(--wd-border)",
        display: "flex", flexDirection: "column", gap: 12,
      }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: "var(--wd-blue)",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          ✓ Mark as Completed
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <SectionLabel>Survey Date *</SectionLabel>
            <input className="wd-input" type="date" value={compDate} max={today}
              onChange={e => setCompDate(e.target.value)} />
          </div>
          <div>
            <SectionLabel>Survey Time</SectionLabel>
            <input className="wd-input" type="time" value={compTime}
              onChange={e => setCompTime(e.target.value)} />
          </div>

        </div>
        <div>
          <SectionLabel>Remarks / Observations</SectionLabel>
          <textarea className="wd-input" rows={2}
            style={{ resize: "vertical", fontSize: 13 }}
            placeholder="Describe findings…"
            value={compRemark} onChange={e => setCompRemark(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setMode(null)} disabled={saving}>Back</button>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={handleComplete}
            disabled={saving || !compDate}>
            {saving ? "Saving…" : "Save & Mark Completed"}
          </button>
        </div>
      </div>
    );
  }
 
  // reschedule
  return (
    <div style={{
      marginTop: 14, paddingTop: 14,
      borderTop: "1px dashed var(--wd-border)",
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-orange)" }}>
        📅 Reschedule Survey
      </div>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
  <div>
    <SectionLabel>New Date *</SectionLabel>
    <input className="wd-input" type="date" value={reschedDate}
      onChange={e => setReschedDate(e.target.value)} />
  </div>
  <div>
    <SectionLabel>New Time</SectionLabel>
    <input className="wd-input" type="time" value={reschedTime}
      onChange={e => setReschedTime(e.target.value)} />
  </div>
</div>
      <div>
        <SectionLabel>Reason / Note</SectionLabel>
        <textarea className="wd-input" rows={2}
          style={{ resize: "vertical", fontSize: 13 }}
          placeholder="Why is it being rescheduled?"
          value={reschedNote} onChange={e => setReschedNote(e.target.value)} />
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setMode(null)} disabled={saving}>Back</button>
        <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={handleReschedule}
          disabled={saving || !reschedDate}>
          {saving ? "Saving…" : "Confirm Reschedule"}
        </button>
      </div>
    </div>
  );
}

function SurveyEditForm({ survey, onClose, onSaved }) {
  const [saving, setSaving]             = useState(false);
  const updateStatus                    = useUpdateSurveyStatus();
  const today                           = new Date().toISOString().slice(0, 10);

  const [status,        setStatus]       = useState(survey.status || "");
  const [surveyorId,    setSurveyorId]   = useState(survey.surveyorId || "");
  const [surveyorName,  setSurveyorName] = useState(survey.surveyorName || "");
  const [scheduledAt, setScheduledAt] = useState(() => {
    if (!survey.scheduledAt && !survey.scheduledDate) return "";
    const d  = new Date(survey.scheduledAt || survey.scheduledDate);
    const yyyy = d.getFullYear();
    const mo   = String(d.getMonth() + 1).padStart(2, "0");
    const dd   = String(d.getDate()).padStart(2, "0");
    const hh   = String(d.getHours()).padStart(2, "0");
    const mm   = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mo}-${dd}T${hh}:${mm}`;
  });

  const [completedDate, setCompletedDate] = useState(() => {
    if (!survey.completedAt) return today;
    const d    = new Date(survey.completedAt);
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const dd   = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });

  const [completedTime, setCompletedTime] = useState(() => {
    if (!survey.completedAt) return new Date().toTimeString().slice(0, 5);
    const d  = new Date(survey.completedAt);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  });
  const [remarks, setRemarks] = useState(survey.remarks || "");

  const STATUS_OPTIONS = [
    "ASSIGNED", "ACCEPTED", "PENDING",
    "RESCHEDULED", "COMPLETED", "REJECTED", "CANCELLED",
  ];

 const handleSave = async () => {
    setSaving(true);
    
    try {
      await updateStatus.mutateAsync({
        surveyId: survey.id,
        status,
        remarks,
        surveyCompletedDate:
          status === "COMPLETED"
            ? `${completedDate}T${completedTime}`
            : null,
       scheduledDate: scheduledAt || null,
        surveyorId:   surveyorId   || null,
        surveyorName: surveyorName || null,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  return (
    <div style={{
      marginTop: 14, paddingTop: 14,
      borderTop: "1px dashed var(--wd-border)",
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-blue)" }}>
        ✏️ Edit Survey
      </div>

      <div>
        <SectionLabel>Status</SectionLabel>
        <select className="wd-input" value={status} onChange={e => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <SectionLabel>Surveyor ID</SectionLabel>
          <input className="wd-input" value={surveyorId}
            onChange={e => setSurveyorId(e.target.value)} placeholder="SYC-…" />
        </div>
        <div>
          <SectionLabel>Surveyor Name</SectionLabel>
          <input className="wd-input" value={surveyorName}
            onChange={e => setSurveyorName(e.target.value)} placeholder="Full name" />
        </div>
      </div>

      <div>
        <SectionLabel>Scheduled At</SectionLabel>
        <input className="wd-input" type="datetime-local" value={scheduledAt}
          onChange={e => setScheduledAt(e.target.value)} />
      </div>

      {status === "COMPLETED" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <SectionLabel>Completed Date *</SectionLabel>
            <input className="wd-input" type="date" value={completedDate}
              max={today} onChange={e => setCompletedDate(e.target.value)} />
          </div>
          <div>
            <SectionLabel>Completed Time</SectionLabel>
            <input className="wd-input" type="time" value={completedTime}
              onChange={e => setCompletedTime(e.target.value)} />
          </div>
        </div>
      )}

      <div>
        <SectionLabel>Remarks</SectionLabel>
        <textarea className="wd-input" rows={2}
          style={{ resize: "vertical", fontSize: 13 }}
          placeholder="Observations or notes…"
          value={remarks} onChange={e => setRemarks(e.target.value)} />
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="wd-btn wd-btn-ghost wd-btn-sm"
          onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button className="wd-btn wd-btn-primary wd-btn-sm"
          onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
export function WaitingForSurveyor({ caseId, onSkipSurveys, skipSaving }) {
  const navigate  = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
 
  const handleCopy = () => {
    navigator.clipboard.writeText(caseId || "").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
 
  return (
    <div style={{
      borderRadius: 14, overflow: "hidden",
      border: "1px solid rgba(59,130,246,.25)",
    }}>
      {/* Header strip */}
      <div style={{
        padding: "12px 18px",
        background: "rgba(59,130,246,.08)",
        borderBottom: "1px solid rgba(59,130,246,.15)",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>⏳</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-blue)" }}>
          Waiting for Spot Surveyor Details
        </span>
        <span style={{
          marginLeft: "auto", fontSize: 11, fontWeight: 600,
          padding: "2px 9px", borderRadius: 20,
          background: "rgba(59,130,246,.12)", color: "var(--wd-blue)",
          border: "1px solid rgba(59,130,246,.2)",
        }}>
          Pending
        </span>
      </div>
 
      {/* Body */}
      <div style={{ padding: "18px 18px 20px", background: "var(--wd-card)" }}>
        <p style={{ fontSize: 13, color: "var(--wd-t2)", margin: "0 0 16px", lineHeight: 1.6 }}>
          Intimation has been sent to the insurance company. Once they assign a spot surveyor,
          you can assign them to this case from the <strong>Surveyors</strong> page using the case ID below.
        </p>
 
        {/* Case ID row */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderRadius: 10,
          background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
          marginBottom: 14,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 }}>
              Case ID
            </div>
            <div style={{ fontFamily: "var(--wd-mono)", fontSize: 15, fontWeight: 700, color: "var(--wd-blue)" }}>
              {caseId || "—"}
            </div>
          </div>
          <button
            onClick={handleCopy}
            className="wd-btn wd-btn-ghost wd-btn-sm"
            style={{
              display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
              transition: "all .15s",
              ...(copied ? { color: "var(--wd-green)", borderColor: "var(--wd-green)" } : {}),
            }}
          >
            {copied
              ? <><span>✓</span> Copied!</>
              : <><CopyIcon /> Copy ID</>
            }
          </button>
        </div>
 
        {/* Navigate CTA */}
        <button
          className="wd-btn wd-btn-primary"
          style={{ width: "100%", justifyContent: "center", display: "flex", alignItems: "center", gap: 8 }}
          onClick={() => navigate("/admin/surveyors",{ state: { prefillCaseId: caseId,returnTo: `/admin/cases/${caseId}`, returnTab: "Insurance"  } })}
        >
          <span>👤</span>
          Assign Spot Surveyor
          <ArrowRightIcon />
        </button>
        {/* ADDED: skip-surveys entry point */}
        {onSkipSurveys && !showSkip && (
          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            style={{ width: "100%", justifyContent: "center", marginTop: 10, color: "var(--wd-t3)" }}
            onClick={() => setShowSkip(true)}
          >
            Not going through insurer for this case? Skip surveys →
          </button>
        )}
         
          {showSkip && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed var(--wd-border)" }}>
            <SkipReasonForm
              warningText="This skips insurance intimation and the entire survey process, and moves the case straight to Workshop Repair. This can't be easily undone."
              confirmLabel="Skip & Move to Workshop"
              saving={false}
              onCancel={() => setShowSkip(false)}
              onConfirm={(reason) => onSkipSurveys(reason)}
            />
          </div>
        )}

      </div>
    </div>
  );
}

export function SurveyCard({ survey, index, showActions, onRefetch }) {
  const [acting, setActing] = useState(false);
  const [editing, setEditing] = useState(false);

  const isDone = survey.status === "COMPLETED";
  const isPending = ["ASSIGNED", "ACCEPTED", "PENDING", "RESCHEDULED"].includes(survey.status);
 
  return (
    <div className="wd-card" style={{ padding: 20 }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "rgba(59,130,246,.13)", color: "#3b82f6",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700,
          }}>
            {index + 1}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {(survey.type || "").replace(/_/g, " ") || "Survey"}
            </div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>
              Assigned: {formatDateTime(survey.assignedAt)}
              {survey.scheduledAt && ` · Scheduled: ${formatDateTime(survey.scheduledAt)}`}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Badge variant={surveyBadgeVariant(survey.status)}>{survey.status}</Badge>
          {showActions && isPending && !acting && (
            <button
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ fontSize: 11 }}
              onClick={() => setActing(true)}
            >
              ✏️ Update
            </button>
          )}
          {isDone && !acting && !editing && (
            <button
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ fontSize: 11 }}
              onClick={() => setEditing(true)}
            >
              ✏️ Edit
            </button>
          )}
        </div>
      </div>
 
      {/* Info grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <InfoRow label="Surveyor ID" value={survey.surveyorId} mono />
        {survey.surveyorName && <InfoRow label="Surveyor Name" value={survey.surveyorName} />}
        {survey.scheduledAt && <InfoRow label="Scheduled At" value={formatDateTime(survey.scheduledAt)} />}
        {isDone && <InfoRow label="Completed" value={formatDateTime(survey.completedAt)} />}
      </div>
 
      {/* Remarks */}
      {survey.remarks && (
        <div style={{
          marginTop: 12, padding: "10px 14px",
          background: "var(--wd-hover)", borderRadius: 10,
        }}>
          <SectionLabel>Remarks</SectionLabel>
          <p style={{ fontSize: 13, color: "var(--wd-t2)", margin: 0, lineHeight: 1.5 }}>
            {survey.remarks}
          </p>
        </div>
      )}
 
      {/* Rejection reason */}
      {survey.rejectionReason && (
        <div style={{
          marginTop: 12, padding: "10px 14px",
          background: "rgba(239,68,68,.08)", borderRadius: 10,
          border: "1px solid rgba(239,68,68,.15)",
        }}>
          <SectionLabel>Rejection Reason</SectionLabel>
          <p style={{ fontSize: 13, color: "var(--wd-red)", margin: 0, lineHeight: 1.5 }}>
            {survey.rejectionReason}
          </p>
        </div>
      )}
 
      {/* Inline action form */}
      {acting && (
        <SurveyActionForm
          survey={survey}
          onClose={() => setActing(false)}
          onSaved={onRefetch}
        />
      )}
      {editing && (
        <SurveyEditForm
          survey={survey}
          onClose={() => setEditing(false)}
          onSaved={onRefetch}
        />
      )}
    </div>
  );
}

export function VehiclePartPickerModal({ open, onClose, caseInfo, workshop }) {
  const navigate = useNavigate();
  if (!open) return null;

  const primeMoverDone = !!workshop?.primeMoverWorkshopId;
  const trailerDone    = !!workshop?.trailerWorkshopId;
  const completeDone   = !!workshop?.workshopId;

  // If both split parts are done, nothing left to assign — caller shouldn't
  // even open this modal, but guard here too
  if (primeMoverDone && trailerDone) return null;

  const goAssign = (part) => {
    onClose();
    navigate("/admin/workshops", {
      state: {
        prefillCaseId: caseInfo.accidentCaseNo,
        returnTo: `/admin/cases/${caseInfo.accidentCaseNo}`,
        returnTab: "Surveys",
        vehiclePart: part,
      },
    });
  };

  const options = [
    // Only show "Complete Vehicle" if neither split part is assigned yet
    ...(!primeMoverDone && !trailerDone && !completeDone ? [{
      part: "COMPLETE",
      icon: "🚛",
      label: "Complete Vehicle",
      desc: "The entire vehicle goes to one workshop for full repairs.",
      color: "#3b82f6",
    }] : []),
    // Only show Prime Mover if not yet assigned
    ...(!primeMoverDone && !completeDone ? [{
      part: "PRIME_MOVER",
      icon: "🚜",
      label: "Prime Mover Only",
      desc: "Assign a workshop for the prime mover. Trailer can be assigned separately.",
      color: "#a855f7",
    }] : []),
    // Only show Trailer if not yet assigned
    ...(!trailerDone && !completeDone ? [{
      part: "TRAILER",
      icon: "🚌",
      label: "Trailer Only",
      desc: "Assign a workshop for the trailer. Prime mover can be assigned separately.",
      color: "#f97316",
    }] : []),
  ];

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1300,
        background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div style={{
        width: "100%", maxWidth: 480, borderRadius: 16,
        background: "var(--wd-surface, #111827)",
        border: "1px solid rgba(99,102,241,.2)",
        boxShadow: "0 24px 64px rgba(0,0,0,.6)", overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,.06)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: "rgba(59,130,246,.12)", border: "1px solid rgba(59,130,246,.25)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
          }}>🔧</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-t1)" }}>Assign Workshop</div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
              {primeMoverDone && !trailerDone && "Prime mover assigned — now assign the trailer"}
              {!primeMoverDone && trailerDone && "Trailer assigned — now assign the prime mover"}
              {!primeMoverDone && !trailerDone && "Select which part of the vehicle needs workshop assignment"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--wd-t3)", padding: 4 }}>✕</button>
        </div>

        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
          {options.map(({ part, icon, label, desc, color }) => (
            <button
              key={part}
              onClick={() => goAssign(part)}
              style={{
                display: "flex", alignItems: "flex-start", gap: 14,
                padding: "14px 16px", borderRadius: 12, cursor: "pointer",
                textAlign: "left", background: `${color}0d`,
                border: `1.5px solid ${color}33`, outline: "none",
                transition: "border-color .15s, box-shadow .15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 0 0 3px ${color}20`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}33`; e.currentTarget.style.boxShadow = "none"; }}
            >
              <div style={{
                width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                background: `${color}18`, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 20,
              }}>{icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)", marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 12, color: "var(--wd-t3)", lineHeight: 1.5 }}>{desc}</div>
              </div>
            </button>
          ))}
        </div>

        <div style={{ padding: "0 20px 16px", display: "flex", justifyContent: "flex-end" }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function AfterSpotSurveyPanel({ services, caseInfo, workshop, onGoToWorkshop, onGoToServices, onRefetch }) {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Refetch when the page becomes visible again (user returns from workshop assignment)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && onRefetch) {
        console.log("Page visible - refetching workshop data");
        onRefetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [onRefetch]);

  // Also refetch when the modal closes (in case user completed assignment and came back)
  useEffect(() => {
    if (!pickerOpen && onRefetch) {
      console.log("Modal closed - refetching workshop data");
      onRefetch();
    }
  }, [pickerOpen, onRefetch]);

  const hasOnSpot = (services || []).length > 0;

  const primeMoverDone = !!workshop?.primeMoverWorkshopId;
  const trailerDone    = !!workshop?.trailerWorkshopId;
  const completeDone   = !!workshop?.workshopId;

  // All assigned when: complete vehicle assigned, OR both split parts assigned
  const allAssigned = completeDone || (primeMoverDone && trailerDone);

  // Show the assign button only if there's still something left to assign
  const canAssignMore = !allAssigned;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{
        fontSize: 12, fontWeight: 700, color: "var(--wd-t3)",
        textTransform: "uppercase", letterSpacing: ".06em",
        paddingBottom: 4, borderBottom: "1px solid var(--wd-border)",
      }}>
        Next Steps
      </div>

      {hasOnSpot && (
        <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(168,85,247,.25)" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
            background: "rgba(168,85,247,.07)", borderBottom: "1px solid rgba(168,85,247,.15)",
          }}>
            <span style={{ fontSize: 15 }}>🛠️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-purple, #a855f7)" }}>
                On-Spot Services Requested
              </div>
              <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
                Customer selected {services.length} on-spot service{services.length > 1 ? "s" : ""} —{" "}
                {services.map(s => s.name || s.type || s).join(", ")}
              </div>
            </div>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11 }} onClick={onGoToServices}>
              Go to Services →
            </button>
          </div>
        </div>
      )}

      {/* Complete vehicle assigned */}
      {completeDone && (
        <WorkshopAssignedRow
          label="Complete Vehicle"
          icon="🚛"
          color="#3b82f6"
          workshop={workshop}
          onView={onGoToWorkshop}
        />
      )}

      {/* Split mode — at least one of prime mover / trailer has been assigned */}
      {!completeDone && (primeMoverDone || trailerDone || (!primeMoverDone && !trailerDone && canAssignMore)) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Prime mover row — only show if trailer is assigned (split mode started) or prime mover is assigned */}
          {(primeMoverDone || trailerDone) && (
            <WorkshopAssignedRow
              label="Prime Mover"
              icon="🚜"
              color="#a855f7"
              workshop={primeMoverDone ? { workshopId: workshop.primeMoverWorkshopId, workshopName: workshop.primeMoverWorkshopName } : null}
              onView={onGoToWorkshop}
              onAssign={!primeMoverDone ? () => setPickerOpen(true) : null}
            />
          )}
          {(primeMoverDone || trailerDone) && (
            <WorkshopAssignedRow
              label="Trailer"
              icon="🚌"
              color="#f97316"
              workshop={trailerDone ? { workshopId: workshop.trailerWorkshopId, workshopName: workshop.trailerWorkshopName } : null}
              onView={onGoToWorkshop}
              onAssign={!trailerDone ? () => setPickerOpen(true) : null}
            />
          )}
        </div>
      )}

      {/* Nothing assigned yet — show single assign button */}
      {canAssignMore && !primeMoverDone && !trailerDone && !completeDone && (
        <div style={{
          borderRadius: 12, overflow: "hidden",
          border: "1px solid rgba(59,130,246,.25)",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
            background: "rgba(59,130,246,.07)", borderBottom: "1px solid rgba(59,130,246,.15)",
          }}>
            <span style={{ fontSize: 15 }}>🔧</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-blue)" }}>Assign Workshop</div>
              <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
                {hasOnSpot
                  ? "Once on-spot services are done, assign a workshop for repairs."
                  : "No on-spot service selected. Assign a workshop to proceed with repairs."}
              </div>
            </div>
            <button
              className="wd-btn wd-btn-primary wd-btn-sm"
              style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}
              onClick={() => setPickerOpen(true)}
            >
              Assign Workshop
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* All workshops assigned — show next step: Assign Surveyor */}
      {allAssigned && (
        <div style={{
          borderRadius: 12, overflow: "hidden",
          border: "1px solid rgba(59,130,246,.25)",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
            background: "rgba(59,130,246,.07)", borderBottom: "1px solid rgba(59,130,246,.15)",
          }}>
            <span style={{ fontSize: 15 }}>👤</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-blue)" }}>Assign Surveyor</div>
              <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
                All workshops assigned. Now assign a surveyor for the repair inspection.
              </div>
            </div>
            <button
              className="wd-btn wd-btn-primary wd-btn-sm"
              style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}
              onClick={() => navigate("/admin/surveyors", {
                state: {
                  prefillCaseId: caseInfo.accidentCaseNo,
                  returnTo: `/admin/cases/${caseInfo.accidentCaseNo}`,
                  returnTab: "Surveys",
                },
              })}
            >
              Go to Surveyors
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Pass workshop so modal knows what's already assigned */}
      <VehiclePartPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        caseInfo={caseInfo}
        workshop={workshop}
      />
    </div>
  );
}

function WorkshopAssignedRow({ label, icon, color, workshop, onView, onAssign }) {
  const assigned = !!workshop?.workshopId;
  return (
    <div style={{
      borderRadius: 12, overflow: "hidden",
      border: assigned ? `1px solid rgba(34,197,94,.25)` : `1px solid ${color}33`,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
        background: assigned ? "rgba(34,197,94,.07)" : `${color}0d`,
        borderBottom: assigned ? "1px solid rgba(34,197,94,.15)" : `1px solid ${color}22`,
      }}>
        <span style={{ fontSize: 15 }}>{assigned ? "✅" : icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: assigned ? "var(--wd-green)" : color }}>
            {label} — {assigned ? "Workshop Assigned" : "Not Assigned"}
          </div>
          <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
            {assigned ? `Workshop ID: ${workshop.workshopId}` : `Tap to assign a workshop for the ${label.toLowerCase()}`}
          </div>
        </div>
        {assigned ? (
          <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11 }} onClick={() => onView?.(workshop.workshopId)}>
            View Workshop →
          </button>
        ) : onAssign ? (
          <button
            className="wd-btn wd-btn-primary wd-btn-sm"
            style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}
            onClick={onAssign}
          >
            Assign
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: ".06em", color: "var(--wd-t3)", marginBottom: 4,
    }}>
      {children}
    </div>
  );
}
 
export function NextStepCard({ icon, color, title, subtitle, action }) {
  return (
    <div style={{
      borderRadius: 12, overflow: "hidden",
      border: `1px solid ${color}33`,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "11px 16px",
        background: `${color}0d`,
        borderBottom: `1px solid ${color}22`,
      }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
    </div>
  );
}

export function WorkshopSurveyPanel({ workshopSurvey, caseInfo }) {
  const navigate = useNavigate();
  const isDone   = workshopSurvey?.status === "COMPLETED" || !!workshopSurvey?.completedAt;
 
  if (isDone) return null; // card already rendered in main list
 
  if (workshopSurvey) {
    // exists but not completed yet — just a status note, card is in the list above
    return null;
  }
 
  // No workshop survey assigned yet
  return (
    <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(234,179,8,.25)" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
        background: "rgba(234,179,8,.07)", borderBottom: "1px solid rgba(234,179,8,.15)",
      }}>
        <span style={{ fontSize: 15 }}>⏳</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-yellow)" }}>
            Workshop / Final Survey Not Assigned Yet
          </div>
          <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
            Assign a surveyor for the final workshop inspection
          </div>
        </div>
        <button
          className="wd-btn wd-btn-primary wd-btn-sm"
          style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}
          onClick={() => navigate("/admin/surveyors",{ state: { prefillCaseId: caseInfo.accidentCaseNo,returnTo: `/admin/cases/${caseInfo.accidentCaseNo}`, returnTab: "Surveys"} })}
        >
          Assign Surveyor
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Small reusable UI pieces
// ─────────────────────────────────────────────────────────────────────────────
function CopyIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  );
}

export function StatusBanner({ raised, date, method }) {
  if (raised) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "14px 18px", borderRadius: 12,
        background: "rgba(34,197,94,.08)", border: "1px solid rgba(34,197,94,.22)",
      }}>
        <span style={{ fontSize: 22 }}>✅</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-green)" }}>Intimation Already Raised</div>
          <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 2 }}>
            {date && `Sent on ${formatDate(date)}`}{method && ` · via ${method}`}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "14px 18px", borderRadius: 12,
      background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.2)",
    }}>
      <span style={{ fontSize: 22 }}>⚠️</span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-red)" }}>Intimation Not Raised Yet</div>
        <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 2 }}>
          Insurance company has not been notified. Raise intimation to get a surveyor assigned.
        </div>
      </div>
    </div>
  );
}

export function OptionCard({ icon, title, desc, tag, tagColor = "var(--wd-blue)", onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, minWidth: 0, padding: "20px 18px", cursor: "pointer",
        textAlign: "left", borderRadius: 12,
        border: "1.5px solid var(--wd-border)",
        background: "var(--wd-card)", outline: "none",
        transition: "border-color .15s, box-shadow .15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--wd-blue)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59,130,246,.1)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--wd-border)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      <div style={{ fontSize: 26, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-t1)", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, color: "var(--wd-t3)", lineHeight: 1.6, marginBottom: 10 }}>{desc}</div>
      {tag && (
        <span style={{
          fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20,
          background: `${tagColor}18`, color: tagColor, border: `1px solid ${tagColor}33`,
        }}>{tag}</span>
      )}
    </button>
  );
}

export function DocRow({ doc, checked, onChange }) {
  const verified = ["VERIFIED", "APPROVED"].includes(doc.status);
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 12px", borderRadius: 10,
      background: checked ? "rgba(59,130,246,.06)" : "var(--wd-hover)",
      border: `1px solid ${checked ? "rgba(59,130,246,.25)" : "var(--wd-border)"}`,
      cursor: "pointer", transition: "all .12s", userSelect: "none",
    }}>
      <input
        type="checkbox" checked={checked}
        onChange={e => onChange(doc.id, e.target.checked)}
        style={{ width: 15, height: 15, cursor: "pointer", accentColor: "var(--wd-blue)" }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>
          {(doc.type || "Document").replace(/_/g, " ")}
        </div>
        <div style={{ fontSize: 11, color: "var(--wd-t3)", fontFamily: "var(--wd-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {doc.fileName || doc.fileUrl || "—"}
        </div>
      </div>
      <span style={{
        fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, flexShrink: 0,
        background: verified ? "rgba(34,197,94,.12)" : "rgba(234,179,8,.12)",
        color: verified ? "var(--wd-green)" : "var(--wd-yellow)",
      }}>
        {doc.status || "PENDING"}
      </span>
    </label>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// Insurance info summary (shown at top and in success state)
// ─────────────────────────────────────────────────────────────────────────────
export function InsuranceInfoCard({ caseInfo, insuranceDetails }) {
  const navigate = useNavigate(); 
  const claimNo = insuranceDetails?.claimNo || caseInfo?.claimNumber || null;
  return (
    <div className="wd-card" style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 22 }}>🛡️</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--wd-t1)" }}>
            {insuranceDetails?.company || caseInfo?.insuranceCompany || "Insurance Company"}
          </div>
          <div style={{ fontSize: 12, color: "var(--wd-t3)", fontFamily: "var(--wd-mono)" }}>
            {caseInfo?.policyNo || insuranceDetails?.policyNo || "Policy number not set"}
          </div>
        </div>
        {claimNo && (
          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            style={{ fontSize: 11, color: "var(--wd-blue)", borderColor: "rgba(59,130,246,.3)", flexShrink: 0 }}
            onClick={() =>
              navigate("/admin/claims", {
                state: {
                  highlightCase: caseInfo?.accidentCaseNo,
                },
              })
            }
          >
            View in Claims →
          </button>
        )}
      </div>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12,
        paddingTop: 14, borderTop: "1px solid var(--wd-border)",
      }}>
        <InfoRow label="Policy Type"    value={insuranceDetails?.policyType || "—"} />
        <InfoRow label="Claim No"       value={claimNo || "—"} mono />
        <InfoRow label="Policy Expiry"  value={formatDate(insuranceDetails?.expiryDate)} />
        <InfoRow label="Surveyor Rep"   value={insuranceDetails?.surveyorAllocated || "—"} />
        <InfoRow label="Estimate"       value={caseInfo?.estimateAmount ? formatINR(caseInfo.estimateAmount) : "—"} />
        <InfoRow label="Insurer Email"  value={insuranceDetails?.email || caseInfo?.insuranceEmail || "—"} />
      </div>
    </div>
  );
}